import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./detail.component.scss";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import CHAT_ICON from "../../assets/images/chat.svg";
import AUDIO_ICON from "../../assets/images/audio.svg";
import ChatTranscriptComponent from "./chat-transcript/chat-transcript.component";
import ChatTimelineComponent from "./chat-timeline/chat-timeline.component";
import Button from '@mui/material/Button';
import MediaService from "../../services/media.service";
import CircularProgress from '@mui/material/CircularProgress';
import { Media } from "../../model/media.model";
import InfographicService from "../../services/infographic.service";
import { Infographic } from "../../model/infographic.model";
import InfographicItemComponent from "./infographic-item/infographic-item.component";
import { useSnackbar } from "../../utility/snackbar.utility";
import CustomSnackbar from "../snackbar/snackbar.component";
import RESTORE_ICON from "../../assets/images/repeat.svg";
import { downloadFileAudio } from "../../utility/download-file.utility";
import { convertDateTime } from "../../utility/function.utility";

const DetailComponent = ({ id }) => {

    const navigate = useNavigate();
    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const audioRef = useRef<HTMLAudioElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [media, setMedia] = useState<Media | null>(null);
    const [infographicList, setInfographicList] = useState<Infographic[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [audio, setAudio] = useState<any>();

    useEffect(() => {
        fetchMediaInfo();

        if (audioRef && audioRef.current) {
            const audio = audioRef.current;
            if (audio) {
                audio.addEventListener('play', handlePlay);
                audio.addEventListener('pause', handlePause);
                audio.addEventListener('ended', handleEnded);
            }
        }
    }, []);

    const fetchMediaInfo = async () => {
        try {
            setIsLoading(true);

            // media
            const mediaResponse = await MediaService.getMediaById(id);
            setMedia(mediaResponse.data);

            // infographic
            const infoResponse = await InfographicService.getInfoByMediaId(id);
            if (infoResponse.status === 200) {
                setInfographicList(infoResponse.data);
            }

            // audio
            const audioUrl = await downloadFileAudio(id);
            setAudio(audioUrl);
        } catch (error) {
            showSnackbar('', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
    };

    const handlePause = () => {
        setIsPlaying(false);
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    const clickBack = () => {
        navigate("/");
    }

    const clickCreateInfographic = () => {
        if (media?.is_archived) {
            showSnackbar('รายการนี้ถูกซ่อนอยู่ ไม่สามารถสร้าง Infographic เพิ่มได้ กรุณากด “นำไปใช้” เพื่อสร้างเพิ่ม', 'error', 4000);
            return;
        }
        navigate(`/infographic/create/${media?.id}`);
    }

    const handleDeleteInfo = (infoId: string) => {
        setInfographicList((prev) => [...prev.filter(item => item.id !== infoId)]);
    }

    const handleRestore = async () => {
        try {
            await MediaService.restoreMedia(id);
            setMedia(prev => {
                if (!prev) return null;
                return { ...prev, is_archived: false };
            });
        } catch (error) {
            showSnackbar('', "error");
        }
    }

    return (
        <>
            {
                isLoading ?
                    <div>
                        <CircularProgress />
                    </div> :
                    <div className="detail-control">
                        <div className="detail-main-control">
                            <div>
                                <Button variant="text" className="button-back-step mb-3" startIcon={<KeyboardArrowLeftIcon />} onClick={clickBack}>
                                    รายการทั้งหมด
                                </Button>
                                <div className="detail-main-title">
                                    <span className="me-2">{media?.topic}</span>
                                    <span className="detail-main-upload-by">
                                        อัปโหลดโดย: {media?.created_by}{media?.created_at && ', ' + convertDateTime(media.created_at)}
                                    </span>
                                </div>
                                <p className="detail-main-subtitle">
                                    {media?.description}
                                </p>
                            </div>
                            <div className="d-flex flex-column justify-content-between align-items-end">
                                <div>
                                    {
                                        media?.is_archived &&
                                        <Button
                                            variant="text"
                                            className="detail-main-restore-button"
                                            startIcon={<img src={RESTORE_ICON} width={20} height={20} />}
                                            onClick={handleRestore}
                                        >
                                            นำกลับไปใช้
                                        </Button>
                                    }
                                </div>

                                {
                                    audio && <audio ref={audioRef} src={audio} className="detail-main-audio" controls />
                                }
                            </div>
                        </div>

                        <div className="detail-content-control">
                            <div>
                                <div className="detail-content-item-control">
                                    <div className="detail-content-item-head-control">
                                        <img src={CHAT_ICON} className="detail-content-item-head-icon"></img>
                                        <span className="detail-content-item-head">เนื้อหาจากการถอดไฟล์เสียง</span>
                                    </div>

                                    <div className="detail-content-item-border-control">
                                        <div className="detail-content-item-border-inside-control">
                                            {
                                                media?.transcripts && media?.transcripts.length > 0 &&
                                                <ChatTranscriptComponent transcriptList={media?.transcripts}></ChatTranscriptComponent>
                                            }

                                        </div>
                                    </div>
                                </div>

                                <div className="detail-content-item-control">
                                    <div className="detail-content-item-head-control">
                                        <img src={AUDIO_ICON} className="detail-content-item-head-icon"></img>
                                        <span className="detail-content-item-head">ไทม์ไลน์ผู้พูด</span>
                                    </div>

                                    <div className="detail-content-item-border-control">
                                        <div className="detail-content-item-border-inside-control">
                                            {
                                                media?.transcripts && media?.transcripts.length > 0 &&
                                                <ChatTimelineComponent transcriptList={media.transcripts} />
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-content-item-control">
                                <div className="detail-content-item-head-control">
                                    <img src={CHAT_ICON} className="detail-content-item-head-icon"></img>
                                    <span className="detail-content-item-head">รายการ Infographic</span>
                                </div>

                                <div className="detail-content-item-border-control">
                                    <div className="detail-content-item-info-control">
                                        <span className="dashboard-create-info-empty-text">
                                            {
                                                (infographicList.length > 0) ? 'Infographic ที่สร้างไว้' : 'ยังไม่ได้สร้าง Infographic'
                                            }
                                        </span>

                                        <Button
                                            variant="contained"
                                            className={`dashboard-create-info-button ${media?.is_archived && 'disabled'}`}
                                            onClick={clickCreateInfographic}
                                        >
                                            สร้าง Infographic
                                        </Button>
                                    </div>

                                    {
                                        infographicList && infographicList.map((item, index) => (
                                            <InfographicItemComponent
                                                key={item.id}
                                                mediaId={id}
                                                no={index + 1}
                                                item={item}
                                                isArchived={media?.is_archived}
                                                onDelete={handleDeleteInfo}
                                            />
                                        ))
                                    }
                                </div>
                            </div>

                        </div>
                    </div>
            }

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    );
};

export default DetailComponent;
